import VariantPicker from '@theme/variant-picker';

/* Keep native option values, request cancellation, DOM morphing and update
 * events. Only this template's picker instances retain the alternate view in
 * their requests. Other products and quick-add pickers are unchanged. */
const selector = 'variant-picker.mwh-puzzle-variant-picker[data-mwh-template-suffix]';
const installed = new WeakSet();

function install(picker) {
  if (!(picker instanceof VariantPicker) || installed.has(picker)) return;
  if (!picker.closest('.mwh-puzzle-product')) return;
  installed.add(picker);

  const nativeBuild = picker.buildRequestUrl;
  const nativeUpdate = picker.updateVariantPicker;
  const nativeUpdateMain = picker.updateMain;

  picker.buildRequestUrl = function (...args) {
    this.querySelector('[data-mwh-variant-error]')?.remove();
    const url = new URL(nativeBuild.apply(this, args), window.location.origin);
    url.searchParams.set('view', this.dataset.mwhTemplateSuffix || 'puzzle-map');

    // Draft previews can carry the theme selection in the query as well as in
    // cookies. Retain it when the native picker rebuilds the product URL.
    const current = new URL(window.location.href);
    for (const key of ['preview_theme_id', 'preview_key', '_fd', 'pb']) {
      if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
    }
    return url.toString();
  };

  const validateResponse = (html) => {
    const incoming = html.querySelector('variant-picker');
    const expectedSuffix = picker.dataset.mwhTemplateSuffix || 'puzzle-map';
    if (incoming?.matches(selector) && incoming.dataset.mwhTemplateSuffix === expectedSuffix) return;

    // A stale/default-template response must never replace these controls or
    // publish an event that replaces the gallery with the original layout.
    let notice = picker.querySelector('[data-mwh-variant-error]');
    if (!notice) {
      notice = document.createElement('p');
      notice.dataset.mwhVariantError = '';
      notice.setAttribute('role', 'status');
      picker.append(notice);
    }
    notice.textContent = 'This option could not be loaded. Please refresh the page and try again.';
    throw new Error('Puzzle variant response did not match the current product template');
  };

  picker.updateVariantPicker = function (html) {
    validateResponse(html);
    return nativeUpdate.call(this, html);
  };

  picker.updateMain = function (html) {
    validateResponse(html);
    return nativeUpdateMain.call(this, html);
  };
}

function scan(root) {
  if (root instanceof Element && root.matches(selector)) install(root);
  root.querySelectorAll?.(selector).forEach(install);
}

scan(document);
new MutationObserver((records) => {
  for (const record of records) for (const node of record.addedNodes) scan(node);
}).observe(document.documentElement, { childList: true, subtree: true });

// Also initialize synchronously before a freshly rendered picker handles its
// first change; no global prototype or fetch interception is used.
document.addEventListener('change', (event) => {
  const picker = event.target instanceof Element ? event.target.closest(selector) : null;
  if (picker) install(picker);
}, { capture: true });
